/*
 * MM Upsell — Mangrove Mountain Spring Water
 * -----------------------------------------------------------------
 * Behaviour-driven upsells. Reads the offer catalogue (Shopify
 * metaobjects, rendered into JSON by sections/mm-upsell.liquid), keeps
 * a small first-party visitor profile in localStorage, and decides which
 * single offer, if any, to show at four moments:
 *
 *   add_to_cart    popup after an item is added
 *   product_dwell  popup after the visitor has been on a product page a while
 *   cart           card inside the cart drawer and cart page
 *   exit_intent    popup when the visitor is about to leave
 *
 * Telemetry uses the MM Springs conversion-intelligence contract
 * (offer_exposed / offer_accepted with {offerId, surface}), sent through
 * window.MMIntelligence when the theme embed exists and through
 * Shopify.analytics.publish('mm:…') otherwise. Offer ids are also stamped
 * onto the cart as hidden attributes so orders in Shopify admin show which
 * offers a customer saw and accepted, with no backend required.
 *
 * Safety posture: every entry point is wrapped. Any failure means no popup
 * and an unchanged storefront. Nothing here can block add-to-cart or
 * checkout, and the companion-required popup always takes precedence.
 *
 * Debug: add ?mm_upsell_debug=1 to any URL to log every decision, and use
 * window.MMUpsellDebug.reset() to clear the visitor profile.
 */

(function () {
  'use strict';

  const configElement = document.getElementById('MMUpsellConfig');
  const modal = document.getElementById('MMUpsellModal');
  const cardTemplate = document.getElementById('MMUpsellCardTemplate');
  if (!configElement || !modal) return;

  let config = null;
  try {
    config = JSON.parse(configElement.textContent || '{}');
  } catch (error) {
    console.warn('[mm-upsell] Could not read config; feature disabled.', error);
    return;
  }

  const settings = config.settings || {};
  const surfacesOn = settings.surfaces || {};
  const page = config.page || {};
  const offers = Array.isArray(config.offers) ? config.offers.filter((o) => o && o.offerId) : [];
  if (!settings.enabled || offers.length === 0) return;

  const SOURCE = 'mm-upsell';
  const PROFILE_KEY = 'mm_upsell_profile';
  const SESSION_MS = 30 * 60 * 1000;
  const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
  const SURFACE_NAMES = {
    add_to_cart: 'upsell_add_to_cart',
    product_dwell: 'upsell_product_dwell',
    cart: 'upsell_cart',
    exit_intent: 'upsell_exit_intent',
  };

  const debugEnabled = (() => {
    try {
      return (
        new URLSearchParams(location.search).get('mm_upsell_debug') === '1' ||
        localStorage.getItem('mm_upsell_debug') === '1'
      );
    } catch (error) {
      return false;
    }
  })();
  const log = (...args) => {
    if (debugEnabled) console.info('[mm-upsell]', ...args);
  };

  /* ---------------------------------------------------------------
   * Small helpers
   * ------------------------------------------------------------- */

  const now = () => Date.now();
  const uuid = () =>
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : 'v-' + Math.random().toString(36).slice(2) + now().toString(36);
  const asList = (value) => (Array.isArray(value) ? value : value === undefined || value === null || value === '' ? [] : [value]);
  const isTouch = () => {
    try {
      return window.matchMedia('(pointer: coarse)').matches;
    } catch (error) {
      return false;
    }
  };
  const deviceType = () => {
    try {
      if (window.matchMedia('(max-width: 767px)').matches) return 'mobile';
      if (window.matchMedia('(max-width: 1023px)').matches) return 'tablet';
    } catch (error) {
      /* fall through */
    }
    return 'desktop';
  };

  /* ---------------------------------------------------------------
   * Visitor profile (first-party, stays in this browser)
   * ------------------------------------------------------------- */

  const emptyProfile = () => ({
    v: 1,
    visitorId: uuid(),
    firstSeen: now(),
    lastSeen: now(),
    sessionCount: 0,
    session: null,
    productViews: {},
    collectionViews: {},
    added: {},
    checkoutStartedCount: 0,
    likelyPurchasedAt: null,
    cartToken: null,
    device: deviceType(),
    firstSource: null,
    lastSource: null,
    offers: {},
  });

  /* Declared before the loadProfile() call below: these are consts, so calling loadProfile()
     above them would hit the temporal dead zone and throw on every page load. */
  const plainObject = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});
  const timestamps = (value) => (Array.isArray(value) ? value.filter((entry) => typeof entry === 'number' && isFinite(entry)) : []);
  const counter = (value) => (typeof value === 'number' && isFinite(value) ? value : 0);

  let profile = loadProfile();

  function loadProfile() {
    try {
      const raw = localStorage.getItem(PROFILE_KEY);
      if (!raw) return emptyProfile();
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || parsed.v !== 1) return emptyProfile();
      const loaded = Object.assign(emptyProfile(), parsed);

      /* Whatever is in localStorage is not necessarily what we last wrote: another script, a
         truncated write, or an older shape. A primitive where an object belongs would throw on
         the first property assignment in recordPageView, and start() would then disable the
         engine on every load in that browser. Re-check every container. */
      loaded.productViews = plainObject(loaded.productViews);
      loaded.collectionViews = plainObject(loaded.collectionViews);
      loaded.added = plainObject(loaded.added);
      loaded.offers = plainObject(loaded.offers);
      loaded.session = plainObject(loaded.session).id ? loaded.session : null;
      loaded.checkoutStartedCount = counter(loaded.checkoutStartedCount);
      loaded.sessionCount = counter(loaded.sessionCount);

      Object.keys(loaded.productViews).forEach((handle) => {
        const entry = plainObject(loaded.productViews[handle]);
        loaded.productViews[handle] = {
          count: counter(entry.count),
          sessions: Array.isArray(entry.sessions) ? entry.sessions.filter((id) => typeof id === 'string') : [],
          last: counter(entry.last),
        };
      });
      Object.keys(loaded.collectionViews).forEach((handle) => {
        const entry = plainObject(loaded.collectionViews[handle]);
        loaded.collectionViews[handle] = { count: counter(entry.count), last: counter(entry.last) };
      });
      Object.keys(loaded.offers).forEach((offerId) => {
        const stats = plainObject(loaded.offers[offerId]);
        loaded.offers[offerId] = {
          exposed: timestamps(stats.exposed),
          dismissed: timestamps(stats.dismissed),
          accepted: timestamps(stats.accepted),
        };
      });
      return loaded;
    } catch (error) {
      return emptyProfile();
    }
  }

  function saveProfile() {
    try {
      profile.lastSeen = now();
      localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    } catch (error) {
      /* Storage unavailable: keep working in memory for this page. */
    }
  }

  function touchSession() {
    const current = profile.session;
    if (!current || !current.lastActivity || now() - current.lastActivity > SESSION_MS) {
      profile.session = {
        id: uuid(),
        startedAt: now(),
        lastActivity: now(),
        shows: 0,
        lastShownAt: 0,
        exitShown: false,
        checkoutMarked: false,
        /* Offer ids already counted as a cart-card exposure this visit. Dawn rebuilds the drawer
           on every add, which destroys and recreates the card; without this the same card was
           counted again on every re-render. */
        cartExposed: {},
      };
      profile.sessionCount = (profile.sessionCount || 0) + 1;
    } else {
      current.lastActivity = now();
    }
  }

  function recordSource() {
    let source = null;
    try {
      const utm = new URLSearchParams(location.search).get('utm_source');
      if (utm) {
        source = utm.slice(0, 80).toLowerCase();
      } else if (document.referrer) {
        const host = new URL(document.referrer).hostname;
        if (host && host !== location.hostname) source = host;
      }
    } catch (error) {
      /* ignore */
    }
    if (source) {
      if (!profile.firstSource) profile.firstSource = source;
      profile.lastSource = source;
    }
  }

  function recordPageView() {
    if (page.type === 'product' && page.handle) {
      const entry = profile.productViews[page.handle] || { count: 0, sessions: [], last: 0 };
      entry.count += 1;
      entry.last = now();
      if (!entry.sessions.includes(profile.session.id)) {
        entry.sessions.push(profile.session.id);
        if (entry.sessions.length > 20) entry.sessions.shift();
      }
      profile.productViews[page.handle] = entry;
    } else if (page.type === 'collection' && page.handle) {
      const entry = profile.collectionViews[page.handle] || { count: 0, last: 0 };
      entry.count += 1;
      entry.last = now();
      profile.collectionViews[page.handle] = entry;
    }
  }

  const offerStats = (offerId) => profile.offers[offerId] || { exposed: [], dismissed: [], accepted: [] };

  function record(kind, offerId) {
    const stats = offerStats(offerId);
    stats[kind] = stats[kind] || [];
    stats[kind].push(now());
    if (stats[kind].length > 20) stats[kind].shift();
    profile.offers[offerId] = stats;
    saveProfile();
  }

  /* ---------------------------------------------------------------
   * Cart
   * ------------------------------------------------------------- */

  const routes = window.routes || {};
  const cartUrl = () => routes.cart_url || '/cart';
  const cartAddUrl = () => routes.cart_add_url || '/cart/add';
  const cartChangeUrl = () => (routes.cart_change_url || '/cart/change') + '.js';
  const cartUpdateUrl = () => (routes.cart_update_url || '/cart/update') + '.js';

  let cartCache = { at: 0, cart: null };

  function fetchCart(fresh) {
    if (!fresh && cartCache.cart && now() - cartCache.at < 2000) return Promise.resolve(cartCache.cart);
    return fetch(cartUrl() + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((response) => response.json())
      .then((cart) => {
        cartCache = { at: now(), cart };
        observeCartToken(cart);
        return cart;
      });
  }

  /* After a completed checkout Shopify issues a new cart token. If we saw a
     checkout start and later find an empty cart with a different token, the
     visitor most likely bought. Recorded as "likely", never as fact. */
  function observeCartToken(cart) {
    if (!cart || !cart.token) return;
    if (
      profile.cartToken &&
      profile.cartToken !== cart.token &&
      cart.item_count === 0 &&
      profile.checkoutStartedCount > 0 &&
      !profile.likelyPurchasedAt
    ) {
      profile.likelyPurchasedAt = now();
      log('marked likely purchased');
    }
    if (profile.cartToken !== cart.token) {
      profile.cartToken = cart.token;
      saveProfile();
    }
  }

  const cartHandles = (cart) => (cart.items || []).map((item) => item.handle);
  const qtyOf = (cart, handles) =>
    (cart.items || []).reduce((total, item) => (handles.includes(item.handle) ? total + (item.quantity || 0) : total), 0);

  /* Element the theme uses to display the cart after an add. */
  const cartElement = () => document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  const sectionsPayload = () => {
    const cart = cartElement();
    if (!cart || typeof cart.getSectionsToRender !== 'function') return {};
    return { sections: cart.getSectionsToRender().map((section) => section.id), sections_url: window.location.pathname };
  };
  const renderCart = (data) => {
    const cart = cartElement();
    if (cart && typeof cart.renderContents === 'function' && data && data.sections) cart.renderContents(data);
  };

  const jsonPost = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then((response) => response.json());

  /* Hidden cart attributes (underscore prefix) travel onto the order. */
  function stampAttribute(key, offerId) {
    fetchCart()
      .then((cart) => {
        const current = (cart.attributes && cart.attributes[key]) || '';
        const ids = current ? String(current).split(',') : [];
        if (ids.includes(offerId)) return null;
        ids.push(offerId);
        while (ids.length > 20) ids.shift();
        const attributes = {};
        attributes[key] = ids.join(',');
        return jsonPost(cartUpdateUrl(), { attributes }).then((updated) => {
          if (updated && updated.token) cartCache = { at: now(), cart: updated };
        });
      })
      .catch((error) => log('attribute stamp failed', error));
  }

  /* ---------------------------------------------------------------
   * Telemetry (contract: offer_exposed / offer_accepted {offerId, surface})
   * ------------------------------------------------------------- */

  function analyticsAllowed() {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') {
        return privacy.analyticsProcessingAllowed() === true;
      }
    } catch (error) {
      /* fall through */
    }
    return true;
  }

  function emit(name, offerId, surface) {
    const properties = { offerId, surface: SURFACE_NAMES[surface] || surface };
    log('emit', name, properties);
    if (!analyticsAllowed()) return;
    try {
      const embed = window.MMIntelligence;
      if (name === 'offer_exposed' && embed && typeof embed.offerExposed === 'function') {
        embed.offerExposed(properties.offerId, properties.surface);
        return;
      }
      if (name === 'offer_accepted' && embed && typeof embed.offerAccepted === 'function') {
        embed.offerAccepted(properties.offerId, properties.surface);
        return;
      }
      const analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') analytics.publish('mm:' + name, properties);
    } catch (error) {
      log('emit failed', error);
    }
  }

  /* ---------------------------------------------------------------
   * Rules
   * ------------------------------------------------------------- */

  const resolveArg = (arg) => (arg === '@page' ? page.handle || '' : arg);

  function fact(name, arg, context) {
    const single = resolveArg(arg);
    const list = asList(arg).map(resolveArg);
    const cart = context.cart || {};
    switch (name) {
      case 'page.type':
        return page.type || 'other';
      case 'page.handle':
        return page.handle || '';
      case 'cart.item_count':
        return cart.item_count || 0;
      case 'cart.subtotal':
        return (cart.items_subtotal_price || 0) / 100;
      case 'cart.has_product':
        return cartHandles(cart).includes(single);
      case 'cart.has_any_product':
        return list.some((handle) => cartHandles(cart).includes(handle));
      case 'cart.qty_of_product':
        return qtyOf(cart, [single]);
      case 'cart.qty_of_products':
        return qtyOf(cart, list);
      case 'profile.session_count':
        return profile.sessionCount || 0;
      case 'profile.product_view_count':
        return (profile.productViews[single] || {}).count || 0;
      case 'profile.product_view_sessions':
        return ((profile.productViews[single] || {}).sessions || []).length;
      case 'profile.collection_view_count':
        return (profile.collectionViews[single] || {}).count || 0;
      case 'profile.added_count':
        return profile.added[single] || 0;
      case 'profile.checkout_started_count':
        return profile.checkoutStartedCount || 0;
      case 'profile.likely_purchased':
        return !!profile.likelyPurchasedAt;
      case 'profile.device':
        return profile.device || deviceType();
      case 'profile.first_source':
        return profile.firstSource || '';
      case 'profile.exposure_count':
        return offerStats(single).exposed.length;
      case 'profile.dismissal_count':
        return offerStats(single).dismissed.length;
      case 'profile.accepted_count':
        return offerStats(single).accepted.length;
      default:
        return undefined;
    }
  }

  function compare(actual, op, expected) {
    switch (op || '==') {
      case '==':
        return actual === expected || String(actual) === String(expected);
      case '!=':
        return !(actual === expected || String(actual) === String(expected));
      case '>':
        return Number(actual) > Number(expected);
      case '>=':
        return Number(actual) >= Number(expected);
      case '<':
        return Number(actual) < Number(expected);
      case '<=':
        return Number(actual) <= Number(expected);
      case 'in':
        return asList(expected).map(String).includes(String(actual));
      case 'not_in':
        return !asList(expected).map(String).includes(String(actual));
      default:
        return false;
    }
  }

  function evaluateCondition(condition, context) {
    if (!condition || typeof condition.fact !== 'string') return { ok: false, why: 'malformed condition' };
    const actual = fact(condition.fact, condition.arg, context);
    if (actual === undefined) return { ok: false, why: 'unknown fact ' + condition.fact };
    const expected = condition.value === undefined ? true : condition.value;
    const ok = compare(actual, condition.op, expected);
    const label =
      condition.fact +
      (condition.arg !== undefined ? '(' + JSON.stringify(condition.arg) + ')' : '') +
      ' = ' +
      JSON.stringify(actual) +
      ' ' +
      (condition.op || '==') +
      ' ' +
      JSON.stringify(expected);
    return { ok, why: label };
  }

  function rulesMatch(offer, context) {
    const rules = offer.rules || {};
    for (const condition of asList(rules.all)) {
      const result = evaluateCondition(condition, context);
      if (!result.ok) return { ok: false, why: 'all: ' + result.why };
    }
    const any = asList(rules.any);
    if (any.length && !any.some((condition) => evaluateCondition(condition, context).ok)) {
      return { ok: false, why: 'any: none matched' };
    }
    return { ok: true, why: 'rules matched' };
  }

  /* "needed" lets an offer say: the customer has N of these products and
     the deal wants a target quantity; expose the difference as [needed]. */
  function computeNeeded(offer, context) {
    const needed = offer.rules && offer.rules.needed;
    if (!needed || !needed.target) return null;
    const have = qtyOf(context.cart, asList(needed.products));
    return Math.max(0, Number(needed.target) - have);
  }

  function inWindow(offer) {
    const time = now();
    const startsAt = offer.startsAt ? Date.parse(offer.startsAt) : null;
    const endsAt = offer.endsAt ? Date.parse(offer.endsAt) : null;
    /* Date.parse returns NaN for anything it does not understand, and every comparison against
       NaN is false — so a mistyped date used to mean "always on", including for an offer that
       was supposed to have ended. Treat an unreadable date as "not scheduled" instead. */
    if (offer.startsAt && isNaN(startsAt)) return false;
    if (offer.endsAt && isNaN(endsAt)) return false;
    if (startsAt !== null && time < startsAt) return false;
    if (endsAt !== null && time > endsAt) return false;
    return true;
  }

  function actionable(offer) {
    if (offer.action === 'link') return !!offer.linkUrl;
    if (offer.action === 'add_variant') {
      if (offer.rules && offer.rules.addTo === 'cart_line') return true;
      return !!(offer.product && offer.product.available && offer.product.variantId);
    }
    return offer.action === 'info';
  }

  function capsAllow(offer, surface) {
    const session = profile.session;
    const stats = offerStats(offer.offerId);
    if (stats.dismissed.some((time) => time >= session.startedAt)) return { ok: false, why: 'dismissed this session' };
    if (offer.action === 'add_variant' && stats.accepted.some((time) => time >= now() - WEEK_MS)) {
      return { ok: false, why: 'accepted within 7 days' };
    }
    /* Cart cards persist for the session once shown; only dismissal or
       acceptance removes them. Popups are rate limited. */
    if (surface === 'cart') return { ok: true, why: 'cart card' };

    /* A blank field means "use the default"; a real 0 means "never show this". Number(0) is
       falsy, so the old `if (capSession && ...)` guards skipped a cap of 0 entirely and treated
       it as unlimited — the exact opposite of what the merchant asked for. A non-numeric value
       falls back to the default rather than becoming NaN, which was also silently unlimited. */
    const capValue = (value, fallback) => {
      if (value === null || value === undefined || value === '') return fallback;
      const parsed = Number(value);
      return isFinite(parsed) && parsed >= 0 ? parsed : fallback;
    };

    const caps = offer.caps || {};
    const capSession = capValue(caps.session, 1);
    const capWeek = capValue(caps.week, 3);
    const gapMinutes = capValue(caps.gapMinutes, Number(settings.minGapMinutes) || 0);

    if (capSession === 0 || capWeek === 0) return { ok: false, why: 'cap set to 0' };

    const sessionShows = stats.exposed.filter((time) => time >= session.startedAt).length;
    const weekShows = stats.exposed.filter((time) => time >= now() - WEEK_MS).length;
    if (sessionShows >= capSession) return { ok: false, why: 'session cap ' + capSession };
    if (weekShows >= capWeek) return { ok: false, why: 'weekly cap ' + capWeek };

    const lastOwn = stats.exposed[stats.exposed.length - 1] || 0;
    if (gapMinutes && now() - lastOwn < gapMinutes * 60000) return { ok: false, why: 'own gap ' + gapMinutes + 'm' };

    const maxPerSession = Number(settings.maxPerSession) || 0;
    if (maxPerSession && session.shows >= maxPerSession) return { ok: false, why: 'global session cap ' + maxPerSession };
    const globalGap = Number(settings.minGapMinutes) || 0;
    if (globalGap && session.lastShownAt && now() - session.lastShownAt < globalGap * 60000) {
      return { ok: false, why: 'global gap ' + globalGap + 'm' };
    }
    return { ok: true, why: 'caps ok' };
  }

  function select(surface, context) {
    const ranked = offers
      .filter((offer) => asList(offer.surfaces).includes(surface))
      .sort((a, b) => (Number(b.priority) || 0) - (Number(a.priority) || 0));

    for (const offer of ranked) {
      if (!inWindow(offer)) {
        log('skip', offer.offerId, surface, 'outside date window');
        continue;
      }
      if (!actionable(offer)) {
        log('skip', offer.offerId, surface, 'not actionable (product unavailable or link missing)');
        continue;
      }
      const caps = capsAllow(offer, surface);
      if (!caps.ok) {
        log('skip', offer.offerId, surface, caps.why);
        continue;
      }
      const rules = rulesMatch(offer, context);
      if (!rules.ok) {
        log('skip', offer.offerId, surface, rules.why);
        continue;
      }
      const needed = computeNeeded(offer, context);
      if (needed === 0) {
        log('skip', offer.offerId, surface, 'nothing needed');
        continue;
      }
      log('select', offer.offerId, surface, needed === null ? '' : 'needed=' + needed);
      return { offer, needed };
    }
    log('no offer for', surface);
    return null;
  }

  const fill = (text, offer, needed) =>
    String(text || '')
      .replace(/\[product\]/g, (offer.product && offer.product.title) || '')
      .replace(/\[price\]/g, (offer.product && offer.product.price) || '')
      .replace(/\[needed\]/g, needed === null || needed === undefined ? '' : String(needed));

  /* ---------------------------------------------------------------
   * Exposure / acceptance / dismissal bookkeeping
   * ------------------------------------------------------------- */

  /* Has this offer's cart card already been counted this visit? Dawn's CartDrawer.renderContents
     replaces the drawer's innerHTML on every add, so the card is destroyed and rebuilt — and the
     rebuilt one used to be counted all over again, inflating offer_exposed, re-stamping the cart
     attribute, and eating the offer's own frequency caps. */
  const cartAlreadyExposed = (offerId) => Boolean(profile.session && profile.session.cartExposed && profile.session.cartExposed[offerId]);

  function expose(offer, surface) {
    if (surface === 'cart') {
      if (cartAlreadyExposed(offer.offerId)) return;
      profile.session.cartExposed = profile.session.cartExposed || {};
      profile.session.cartExposed[offer.offerId] = now();
    }
    record('exposed', offer.offerId);
    if (surface !== 'cart') {
      profile.session.shows += 1;
      profile.session.lastShownAt = now();
    }
    saveProfile();
    emit('offer_exposed', offer.offerId, surface);
    stampAttribute('_mm_offers_exposed', offer.offerId);
  }

  function dismiss(offer, surface) {
    record('dismissed', offer.offerId);
    emit('offer_dismissed', offer.offerId, surface);
  }

  function accept(offer, surface, needed, ui) {
    const done = () => {
      record('accepted', offer.offerId);
      emit('offer_accepted', offer.offerId, surface);
      stampAttribute('_mm_offers_accepted', offer.offerId);
    };

    if (offer.action === 'link' || offer.action === 'info') {
      /* Busy first: neither branch has a network call to disable the button, so a double tap
         recorded the acceptance and emitted offer_accepted twice. */
      ui.busy(true);
      done();
      if (offer.action === 'info') return Promise.resolve(true);
      /* The web pixel batches events and its sandbox dies with the page, so navigating in the
         same tick loses the offer_accepted just emitted — and two of the six live offers are
         link offers, so that was most of the acceptance data. 300 ms is imperceptible and lets
         the flush start. */
      setTimeout(() => { window.location.href = offer.linkUrl; }, 300);
      return Promise.resolve(true);
    }

    const rules = offer.rules || {};
    const quantity = rules.quantity === 'needed' ? Math.max(1, needed || 1) : Math.max(1, Number(rules.quantity) || 1);

    ui.busy(true);
    const request =
      rules.addTo === 'cart_line'
        ? fetchCart(true).then((cart) => {
            const products = asList(rules.needed && rules.needed.products);
            const line = (cart.items || [])
              .filter((item) => products.includes(item.handle))
              .sort((a, b) => b.quantity - a.quantity)[0];
            if (!line) throw new Error('No matching cart line');
            return jsonPost(cartChangeUrl(), Object.assign({ id: line.key, quantity: line.quantity + quantity }, sectionsPayload()));
          })
        : jsonPost(
            cartAddUrl(),
            Object.assign(
              { items: [{ id: Number(offer.product.variantId), quantity, properties: { _mm_offer: offer.offerId } }] },
              sectionsPayload()
            )
          );

    return request
      .then((data) => {
        if (data && data.status) throw new Error(data.description || data.message || 'Could not add');
        done();
        /* The line is in the cart from here on. Everything below is presentation, so a throw in
           it must not fall through to the .catch and tell the shopper the add failed — they
           would try again and buy two. */
        try {
          renderCart(data);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, productVariantId: offer.product && offer.product.variantId, cartData: data });
          }
          fetchCart(true).then(refreshCartCards).catch(() => {});
        } catch (error) {
          log('added, but the cart display could not refresh', error);
        }
        return true;
      })
      .catch((error) => {
        log('accept failed', error);
        ui.error('Sorry, that could not be added just now. Please try again.');
        return false;
      })
      .finally(() => ui.busy(false));
  }

  /* ---------------------------------------------------------------
   * Popup
   * ------------------------------------------------------------- */

  const el = {
    dialog: modal.querySelector('[data-mm-dialog]'),
    eyebrow: modal.querySelector('[data-mm-eyebrow]'),
    heading: modal.querySelector('[data-mm-heading]'),
    body: modal.querySelector('[data-mm-body]'),
    product: modal.querySelector('[data-mm-product]'),
    image: modal.querySelector('[data-mm-image]'),
    name: modal.querySelector('[data-mm-name]'),
    price: modal.querySelector('[data-mm-price]'),
    error: modal.querySelector('[data-mm-error]'),
    cta: modal.querySelector('[data-mm-cta]'),
    ctaText: modal.querySelector('[data-mm-cta-text]'),
    dismiss: modal.querySelector('[data-mm-dismiss]'),
    close: modal.querySelector('[data-mm-close]'),
    footnote: modal.querySelector('[data-mm-footnote]'),
  };

  let active = null;
  let lastFocused = null;
  const modalOpen = () => !modal.hidden;
  const companionOpen = () => {
    const companion = document.getElementById('CompanionRequiredModal');
    return !!companion && !companion.hidden;
  };

  const modalUi = {
    busy(state) {
      if (!el.cta) return;
      if (state) {
        el.cta.setAttribute('aria-disabled', 'true');
        el.cta.classList.add('is-loading');
      } else {
        el.cta.removeAttribute('aria-disabled');
        el.cta.classList.remove('is-loading');
      }
    },
    error(message) {
      if (!el.error) return;
      el.error.textContent = message || '';
      el.error.hidden = !message;
    },
  };

  function openModal(offer, surface, needed) {
    active = { offer, surface, needed };
    lastFocused = document.activeElement;
    modalUi.error('');
    /* The modal element is reused, so a busy state left over from the previous offer would
       render the button permanently un-clickable. */
    modalUi.busy(false);

    if (el.eyebrow) el.eyebrow.textContent = offer.eyebrow || '';
    if (el.heading) el.heading.textContent = fill(offer.headline, offer, needed);
    if (el.body) el.body.textContent = fill(offer.body, offer, needed);
    if (el.footnote) el.footnote.textContent = fill(offer.footnote, offer, needed);
    if (el.dismiss) el.dismiss.textContent = offer.dismissLabel || 'No thanks';
    if (el.ctaText) el.ctaText.textContent = fill(offer.ctaLabel, offer, needed);

    const product = offer.product;
    if (el.product) el.product.hidden = !product;
    if (product) {
      if (el.name) el.name.textContent = product.title || '';
      if (el.price) el.price.textContent = product.price || '';
      if (el.image) {
        if (product.image) {
          el.image.src = product.image;
          el.image.alt = product.title || '';
          el.image.hidden = false;
        } else {
          el.image.removeAttribute('src');
          el.image.hidden = true;
        }
      }
    }

    modal.hidden = false;
    window.requestAnimationFrame(() => modal.classList.add('is-active'));
    document.body.classList.add('overflow-hidden');

    const focusTarget = el.cta || el.dismiss || el.close;
    if (typeof trapFocus === 'function') trapFocus(el.dialog || modal, focusTarget);
    else if (focusTarget) focusTarget.focus();

    expose(offer, surface);
  }

  function closeModal() {
    modal.classList.remove('is-active');
    modal.hidden = true;
    active = null;

    const drawer = document.querySelector('cart-drawer.active');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(drawer ? null : lastFocused);
    if (drawer) {
      /* Hand focus back to the drawer, which still owns the scroll lock. */
      const drawerInner = document.getElementById('CartDrawer') || drawer;
      if (typeof trapFocus === 'function') trapFocus(drawerInner, drawer.querySelector('.drawer__close'));
    } else {
      document.body.classList.remove('overflow-hidden');
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }
  }

  if (el.cta) {
    el.cta.addEventListener('click', () => {
      if (!active || el.cta.getAttribute('aria-disabled') === 'true') return;
      const current = active;
      accept(current.offer, current.surface, current.needed, modalUi).then((ok) => {
        if (ok && current.offer.action !== 'link') closeModal();
      });
    });
  }
  if (el.dismiss) {
    el.dismiss.addEventListener('click', () => {
      if (active) dismiss(active.offer, active.surface);
      closeModal();
    });
  }
  /* Closing by X, backdrop or Escape is a dismissal too. Counting only the "No thanks" button
     under-reported offer_dismissed and left the "dismissed this session" rule unarmed, so an
     offer the shopper had just closed could be shown to them again. */
  const dismissAndClose = () => {
    if (active) dismiss(active.offer, active.surface);
    closeModal();
  };
  if (el.close) el.close.addEventListener('click', dismissAndClose);
  modal.addEventListener('click', (event) => {
    if (event.target === modal) dismissAndClose();
  });
  modal.addEventListener('keyup', (event) => {
    if (event.code === 'Escape') dismissAndClose();
  });

  /* Resolves to whether a popup was actually opened. Exit intent needs to know: it has consumed
     the visitor's back press to get here, and if nothing is going to be shown it has to give
     that navigation back. */
  function evaluate(surface) {
    if (!surfacesOn[surface]) return Promise.resolve(false);
    if (modalOpen() || companionOpen()) {
      log('busy, skip', surface);
      return Promise.resolve(false);
    }
    touchSession();
    return fetchCart()
      .then((cart) => {
        const pick = select(surface, { cart });
        if (pick && !modalOpen() && !companionOpen()) {
          openModal(pick.offer, surface, pick.needed);
          return true;
        }
        return false;
      })
      .catch((error) => {
        log('evaluate failed', error);
        return false;
      });
  }

  /* ---------------------------------------------------------------
   * Cart card (drawer + cart page)
   * ------------------------------------------------------------- */

  function cardHosts() {
    const hosts = [];
    const drawerFooter = document.querySelector('#CartDrawer .drawer__footer');
    if (drawerFooter) hosts.push({ key: 'drawer', el: drawerFooter, where: 'beforebegin' });
    if (page.type === 'cart') {
      const pageWidth = document.querySelector('cart-items .page-width');
      const cartItems = document.querySelector('cart-items');
      if (pageWidth) hosts.push({ key: 'page', el: pageWidth, where: 'beforeend' });
      else if (cartItems) hosts.push({ key: 'page', el: cartItems, where: 'afterend' });
    }
    return hosts;
  }

  function buildCard(offer, needed, hostKey) {
    if (!cardTemplate || !cardTemplate.content) return null;
    const fragment = cardTemplate.content.cloneNode(true);
    const card = fragment.querySelector('.mm-upsell-card');
    if (!card) return null;
    card.dataset.mmHost = hostKey;
    card.dataset.mmOffer = offer.offerId;
    card.dataset.mmNeeded = needed === null ? '' : String(needed);

    const set = (selector, text) => {
      const node = card.querySelector(selector);
      if (node) node.textContent = text || '';
    };
    set('[data-mm-card-eyebrow]', offer.eyebrow);
    set('[data-mm-card-headline]', fill(offer.headline, offer, needed));
    set('[data-mm-card-body]', fill(offer.body, offer, needed));
    set('[data-mm-card-cta-text]', fill(offer.ctaLabel, offer, needed));
    set('[data-mm-card-dismiss]', offer.dismissLabel || 'No thanks');

    const image = card.querySelector('[data-mm-card-image]');
    if (image) {
      if (offer.product && offer.product.image) {
        image.src = offer.product.image;
        image.alt = offer.product.title || '';
        image.hidden = false;
      } else {
        image.hidden = true;
      }
    }

    const cta = card.querySelector('[data-mm-card-cta]');
    const errorNode = card.querySelector('[data-mm-card-error]');
    const ui = {
      busy(state) {
        if (!cta) return;
        if (state) {
          cta.setAttribute('aria-disabled', 'true');
          cta.classList.add('is-loading');
        } else {
          cta.removeAttribute('aria-disabled');
          cta.classList.remove('is-loading');
        }
      },
      error(message) {
        if (!errorNode) return;
        errorNode.textContent = message || '';
        errorNode.hidden = !message;
      },
    };

    if (cta) {
      cta.addEventListener('click', () => {
        if (cta.getAttribute('aria-disabled') === 'true') return;
        accept(offer, 'cart', needed, ui).then((ok) => {
          if (ok && offer.action !== 'link') card.remove();
        });
      });
    }
    const dismissButton = card.querySelector('[data-mm-card-dismiss]');
    if (dismissButton) {
      dismissButton.addEventListener('click', () => {
        dismiss(offer, 'cart');
        document.querySelectorAll('.mm-upsell-card[data-mm-offer="' + CSS.escape(offer.offerId) + '"]').forEach((node) => node.remove());
      });
    }

    /* Already counted this visit — the card is being rebuilt after a drawer re-render, not
       shown for the first time. Starting another poller would also leave one 400 ms interval
       running per re-render. */
    if (!cartAlreadyExposed(offer.offerId)) watchExposure(card, offer);
    return card;
  }

  /* Count an exposure only once the card has actually been seen: at least
     half of it inside the viewport, in a visible tab, in a visible drawer,
     for about a second (the same rule the measurement spec uses for
     important sections). Polled rather than observed, because the cart
     drawer slides in with a transform and the card may already exist
     before the drawer opens. */
  function watchExposure(card, offer) {
    let seenTicks = 0;
    const startedAt = now();
    const tick = setInterval(() => {
      if (!card.isConnected || now() - startedAt > 300000) {
        clearInterval(tick);
        return;
      }
      if (document.visibilityState !== 'visible' || getComputedStyle(card).visibility !== 'visible') {
        seenTicks = 0;
        return;
      }
      const rect = card.getBoundingClientRect();
      const area = rect.width * rect.height;
      const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
      const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
      const ratio = area > 0 ? (visibleWidth * visibleHeight) / area : 0;
      seenTicks = ratio >= 0.5 ? seenTicks + 1 : 0;
      if (seenTicks >= 3) {
        clearInterval(tick);
        expose(offer, 'cart');
      }
    }, 400);
  }

  let refreshing = false;
  function refreshCartCards(cart) {
    if (!surfacesOn.cart || refreshing) return;
    const hosts = cardHosts();
    if (!hosts.length) return;
    refreshing = true;
    try {
      touchSession();
      const pick = select('cart', { cart });
      hosts.forEach((host) => {
        const existing = document.querySelector('.mm-upsell-card[data-mm-host="' + host.key + '"]');
        if (!pick) {
          if (existing) existing.remove();
          return;
        }
        const neededText = pick.needed === null ? '' : String(pick.needed);
        if (existing && existing.dataset.mmOffer === pick.offer.offerId && existing.dataset.mmNeeded === neededText) return;
        if (existing) existing.remove();
        const card = buildCard(pick.offer, pick.needed, host.key);
        if (card) host.el.insertAdjacentElement(host.where, card);
      });
    } catch (error) {
      log('card refresh failed', error);
    } finally {
      refreshing = false;
    }
  }

  function watchDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !surfacesOn.cart) return;
    let pending = null;
    const schedule = () => {
      if (pending) clearTimeout(pending);
      pending = setTimeout(() => {
        pending = null;
        fetchCart().then(refreshCartCards).catch(() => {});
      }, 180);
    };
    new MutationObserver((mutations) => {
      /* Ignore mutations caused purely by our own card insertion. */
      const external = mutations.some((mutation) => {
        if (mutation.type === 'attributes') return true;
        return Array.from(mutation.addedNodes).concat(Array.from(mutation.removedNodes)).some((node) => {
          return !(node.nodeType === 1 && node.classList && node.classList.contains('mm-upsell-card'));
        });
      });
      if (external) schedule();
    }).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  /* ---------------------------------------------------------------
   * Surfaces
   * ------------------------------------------------------------- */

  /* The companion popup ("the ceramic pot is not included") always wins: the two must never
     stack. It decides asynchronously, because it checks the cart first, so waiting a fixed
     700 ms and assuming it was not coming lost the race whenever /cart.js was slow — on mobile,
     or once this store's rate limiting kicks in — and both popups opened, ours on top.
     It now sets `window.MMCompanion.pending` synchronously in the same cartUpdate tick and
     announces its decision, so there is nothing left to guess. */
  function whenCompanionClosed(callback) {
    let settled = false;
    let fallbackTimer = null;

    const finish = () => {
      if (settled) return;
      settled = true;
      if (fallbackTimer) clearTimeout(fallbackTimer);
      document.removeEventListener('companion-required:decided', onDecided);
      callback();
    };

    const waitForClose = () => {
      const companion = document.getElementById('CompanionRequiredModal');
      if (!companion || companion.hidden) return finish();
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      const observer = new MutationObserver(() => {
        if (companion.hidden) {
          observer.disconnect();
          setTimeout(finish, 250);
        }
      });
      observer.observe(companion, { attributes: true, attributeFilter: ['hidden'] });
      /* Never wait forever on a modal the shopper simply left open. */
      setTimeout(() => {
        observer.disconnect();
        finish();
      }, 120000);
    };

    function onDecided(event) {
      if (event && event.detail && event.detail.opened) waitForClose();
      else finish();
    }

    if (companionOpen()) return waitForClose();
    /* Set synchronously by the companion script while handling this same cartUpdate, so if it is
       not pending there is genuinely nothing to wait for. */
    if (!(window.MMCompanion && window.MMCompanion.pending)) return finish();

    document.addEventListener('companion-required:decided', onDecided, { once: true });
    /* Only reached if the companion script errored after setting the flag. */
    fallbackTimer = setTimeout(() => {
      if (companionOpen()) waitForClose();
      else finish();
    }, 5000);
  }

  function onCartUpdate(payload) {
    if (!payload || payload.source === SOURCE) return;
    /* Dawn's cartUpdate already carries the whole cart. Re-fetching it cost a round trip per
       add on a store that returns 429 (and then a Turnstile challenge) after a burst of
       /cart/*.js calls — at which point Dawn's own cart stops working too. */
    const supplied = payload.cartData && Array.isArray(payload.cartData.items) && payload.cartData.token ? payload.cartData : null;
    if (supplied) cartCache = { at: now(), cart: supplied };
    (supplied ? Promise.resolve(supplied) : fetchCart(true))
      .then((cart) => {
        const variantId = payload.productVariantId || payload.variantId;
        if (variantId) {
          const item = (cart.items || []).find((line) => String(line.variant_id) === String(variantId));
          if (item && payload.source === 'product-form') {
            profile.added[item.handle] = (profile.added[item.handle] || 0) + 1;
            saveProfile();
          }
        }
        setTimeout(() => fetchCart().then(refreshCartCards).catch(() => {}), 250);
        if (payload.source === 'companion-required') return; /* they just accepted a companion prompt; don't stack */
        if (payload.source !== 'product-form' && payload.source !== 'quick-add') return;
        if (!surfacesOn.add_to_cart) return;
        whenCompanionClosed(() => setTimeout(() => evaluate('add_to_cart'), 350));
      })
      .catch((error) => log('cartUpdate failed', error));
  }

  function wireCheckoutIntent() {
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target && event.target.closest ? event.target.closest(
          '#CartDrawer-Checkout, #checkout, button[name="checkout"], form[action$="/checkout"] [type="submit"], .shopify-payment-button__button, [data-shopify="payment-button"] button'
        ) : null;
        if (!target) return;
        touchSession();
        if (!profile.session.checkoutMarked) {
          profile.session.checkoutMarked = true;
          profile.checkoutStartedCount = (profile.checkoutStartedCount || 0) + 1;
          saveProfile();
          log('checkout started');
        }
      },
      true
    );
  }

  function wireDwell() {
    if (!surfacesOn.product_dwell || page.type !== 'product') return;
    const delay = Math.max(3, Number(settings.dwellSeconds) || 12) * 1000;
    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      evaluate('product_dwell');
    };
    setTimeout(() => {
      if (document.visibilityState === 'visible') fire();
      else document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && fire(), { once: true });
    }, delay);
  }

  /* Could any exit-intent offer plausibly fire on this page, right now? Only page- and
     profile-level facts are checked: cart facts would need a network round trip we cannot do
     before deciding whether to install the back-button guard. An offer gated solely on the cart
     is therefore treated as possible, which is the safe direction. */
  function exitIntentPossible() {
    return offers.some((offer) => {
      if (!asList(offer.surfaces).includes('exit_intent')) return false;
      if (!inWindow(offer) || !actionable(offer)) return false;
      if (!capsAllow(offer, 'exit_intent').ok) return false;
      return asList((offer.rules || {}).all).every((condition) => {
        if (!condition || typeof condition.fact !== 'string') return true;
        const scope = condition.fact.split('.')[0];
        if (scope !== 'page' && scope !== 'profile') return true;
        return evaluateCondition(condition, { cart: {} }).ok;
      });
    });
  }

  function wireExitIntent() {
    if (!surfacesOn.exit_intent) return;
    if (page.type !== 'product' && page.type !== 'cart') return;
    if (window.Shopify && window.Shopify.designMode) return;

    const fire = () => {
      touchSession();
      if (profile.session.exitShown) return Promise.resolve(false);
      profile.session.exitShown = true;
      saveProfile();
      return evaluate('exit_intent');
    };

    document.addEventListener('mouseleave', (event) => {
      if (event.clientY <= 0 && !isTouch()) fire();
    });

    if (!isTouch()) return;

    /* Mobile back-intent. The guard is a history entry we push so the first back press lands
       here instead of leaving the site.
       Two rules make it safe, and both were missing:
       1. Only push it when an offer could actually appear. Before, every product and cart page
          pushed one, so on the ceramic dispenser page — which no exit-intent offer targets —
          the shopper's back press was swallowed and nothing happened. Only one of the six live
          offers uses exit intent, and only on two bottle pages.
       2. If nothing is shown after all, complete the navigation the shopper asked for. */
    if (profile.session.exitShown) return;
    if (!exitIntentPossible()) {
      log('exit intent: no offer could match this page, leaving the back button alone');
      return;
    }

    try {
      if (history.state && history.state.mmUpsellGuard) return;
      history.pushState({ mmUpsellGuard: true }, '');
      window.addEventListener(
        'popstate',
        (event) => {
          if (event.state && event.state.mmUpsellGuard) return;
          fire().then((shown) => {
            if (!shown) history.back();
          });
        },
        { once: true }
      );
    } catch (error) {
      /* History unavailable: desktop-style exit only. */
    }
  }

  /* ---------------------------------------------------------------
   * Start
   * ------------------------------------------------------------- */

  function start() {
    try {
      touchSession();
      recordSource();
      profile.device = deviceType();
      recordPageView();
      saveProfile();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
          try {
            onCartUpdate(payload);
          } catch (error) {
            log('cartUpdate handler failed', error);
          }
        });
      }

      wireCheckoutIntent();
      wireDwell();
      wireExitIntent();
      watchDrawer();
      /* Only fetch the cart on load if a cart card could actually be placed. This ran on every
         page for every visitor, including ones with no cart at all. */
      if (surfacesOn.cart) fetchCart().then(refreshCartCards).catch(() => {});

      if (debugEnabled) {
        window.MMUpsellDebug = Object.freeze({
          profile: () => JSON.parse(JSON.stringify(profile)),
          offers: () => offers,
          evaluate,
          reset: () => {
            try {
              localStorage.removeItem(PROFILE_KEY);
            } catch (error) {
              /* ignore */
            }
            profile = emptyProfile();
            touchSession();
            saveProfile();
            return 'profile reset';
          },
        });
        log('ready', { offers: offers.map((o) => o.offerId), page, settings });
      }
    } catch (error) {
      /* Clear the stored profile so the next page load starts clean. Without this, a profile
         that trips start() keeps tripping it, and the engine stays dead in that browser. */
      try {
        localStorage.removeItem(PROFILE_KEY);
      } catch (ignored) {
        /* Storage unavailable; nothing further to do. */
      }
      console.warn('[mm-upsell] Disabled after start-up error; stored profile cleared for the next load.', error);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
